import {
  getCanonicalDocumentKey,
  getFrontendManagedSpecialFormCards,
} from "../components/permitForms/specialFormsMap";

const getCanonicalFormKey = (form) =>
  getCanonicalDocumentKey(typeof form === "string" ? { form_id: form } : form);

// Energy Form R402 is a legacy frontend form. It does not come from the
// permit-forms API, but must remain selectable for every county.
export const LEGACY_GLOBAL_PERMIT_FORMS = getFrontendManagedSpecialFormCards();

const getUniqueFormsByCanonicalKey = (forms = []) => {
  const seenFormKeys = new Set();

  return forms.filter((form) => {
    const formKey = getCanonicalFormKey(form);
    if (!formKey || seenFormKeys.has(formKey)) return false;

    seenFormKeys.add(formKey);
    return true;
  });
};

export const getCitiesForCounty = (availablePermitForms, currentCounty) => {
  // Helper function to format city names
  const formatCityName = (city) => {
    if (city) {
      return city
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } else {
      return "";
    }
  };

  // Find the data for the current county
  const countyData =
    availablePermitForms.find((item) => item[currentCounty])?.[currentCounty] ||
    {};

  // Create a list of cities by filtering out empty arrays and formatting names
  const cities = Object.keys(countyData)
    .filter(
      (city) =>
        city !== "county_specific" && city !== "noc" && city !== "noc_by_city",
    )
    .map((city) => formatCityName(city));

  // Return the filtered and formatted cities list
  return cities;
};

// Counties from florida_scraping_strategy where NOC can be deselected
// Keep this list centralized so future county additions only need this update.
export const OPTIONAL_NOC_COUNTIES = [
  "duval",
  "st_johns",
  "clay",
  "polk",
  "flagler",
  "hillsborough",
  "pinellas",
  "seminole",
  "broward",
  "collier",
  "nassau",
  "orange",
  "baker",
  "sarasota",
  "charlotte",
  "manatee",
  "lee",
];

const COUNTY_SLUG_ALIASES = {
  saint_johns: "st_johns",
  saint_lucie: "st_lucie",
};

export const normalizeCountySlug = (county = "") => {
  const normalizedCounty = county
    .trim()
    .toLowerCase()
    .replace(/[.'’]/g, "")
    .replace(/[\s-]+/g, "_")
    .replace(/_county$/, "");

  return COUNTY_SLUG_ALIASES[normalizedCounty] || normalizedCounty;
};

export const findCountyByName = (counties = [], countyName = "") => {
  const normalizedCountyName = normalizeCountySlug(countyName);

  if (!normalizedCountyName || !Array.isArray(counties)) {
    return undefined;
  }

  return counties.find(
    (county) => normalizeCountySlug(county?.name) === normalizedCountyName,
  );
};

export const isNocOptionalForCounty = (county = "") => {
  return OPTIONAL_NOC_COUNTIES.includes(normalizeCountySlug(county));
};

// Counties that support property database fetch flow when NOC is not selected.
// Keep aligned with florida_scraping_strategy coverage.
export const PROPERTY_DATABASE_COUNTIES = [...OPTIONAL_NOC_COUNTIES];

export const isPropertyDatabaseCounty = (county = "") => {
  return PROPERTY_DATABASE_COUNTIES.includes(normalizeCountySlug(county));
};

// Helper function to revert formatted city name to original format
const revertCityName = (formattedCity) => {
  if (formattedCity) {
    return formattedCity?.toLowerCase().replace(/ /g, "_");
  } else {
    return "";
  }
};

export const getCombinedListForCity = (
  availablePermitForms = [],
  currentCounty,
  selectedCity,
) => {
  // Revert the formatted city name to the original format
  const originalCityName = revertCityName(selectedCity);

  // Get the "Global" list of objects
  const globalData = availablePermitForms.find((item) => item.Global) || {};
  const globalList = getUniqueFormsByCanonicalKey([
    ...(globalData.Global || []),
    ...LEGACY_GLOBAL_PERMIT_FORMS,
  ]);

  // Get the data for the current county
  const countyData =
    availablePermitForms.find((item) => item[currentCounty])?.[currentCounty] ||
    {};

  // Get the permits for the selected city
  const cityPermits = countyData[originalCityName] || [];

  // Combine the "Global" list with the city's permits
  const cityFormNames = new Set(cityPermits.map(getCanonicalFormKey));
  const combinedPermits = [
    ...cityPermits,
    ...globalList.filter(
      (globalForm) => !cityFormNames.has(getCanonicalFormKey(globalForm)),
    ),
  ];

  // Return an object for the selected city with combined permits
  return {
    city: selectedCity,
    permits: combinedPermits,
  };
};

export const getCombinedList = (
  availablePermitForms = [],
  currentCounty,
  selectedCity,
  selectedForms,
  setSelectedForms,
  oldSelectedForms,
  formsOrder,
  availablePermitTypes = [],
  selectedPermitType = null,
  isPrivateProvider = null,
  permitTypeFormMap = {},
) => {
  const filterPP = (arr, isPrivateProvider) =>
    isPrivateProvider ? arr || [] : (arr || []).filter((f) => !f?.is_pp);

  // Revert the formatted city name to the original format
  const originalCityName = revertCityName(selectedCity);

  // Get the "Global" list of objects
  // Get the data for the current county
  const countyData =
    availablePermitForms.find((item) => item[currentCounty])?.[currentCounty] ||
    {};

  let nocId = countyData?.noc?.form_id;
  let nocImg = countyData?.noc?.img || countyData?.noc?.image_urls;

  // Get the permits for the selected city
  let cityPermits = selectedCity
    ? filterPP(countyData[originalCityName], isPrivateProvider)
    : [];

  const cityFormNames = new Set(
    Array.isArray(cityPermits) ? cityPermits.map(getCanonicalFormKey) : [],
  );
  const countySpecificPermits = filterPP(
    countyData.county_specific,
    isPrivateProvider,
  );
  const countySpecificNames = new Set(
    Array.isArray(countySpecificPermits)
      ? countySpecificPermits.map(getCanonicalFormKey)
      : [],
  );

  const globalData = availablePermitForms.find((item) => item.Global) || {};
  const globalList = getUniqueFormsByCanonicalKey([
    ...filterPP(globalData.Global, isPrivateProvider),
    ...LEGACY_GLOBAL_PERMIT_FORMS,
  ]).filter(
    (form) =>
      form?.form_id &&
      !cityFormNames.has(getCanonicalFormKey(form)) &&
      !countySpecificNames.has(getCanonicalFormKey(form)),
  );

  // Initialize combinedPermits with the global list
  let combinedPermits = [...globalList];

  // Append city-specific permits if a city is selected
  if (selectedCity) {
    combinedPermits = [...cityPermits, ...combinedPermits];
  }

  // Append "county_specific" if no city is selected, or if city-specific permits don't already include the form names from county-specific
  if (!selectedCity) {
    combinedPermits = [...combinedPermits, ...countySpecificPermits];
  } else {
    // Filter out any county-specific permits that also exist in cityPermits by form name
    combinedPermits = [
      ...combinedPermits,
      ...countySpecificPermits.filter(
        (countyForm) =>
          !cityPermits.some(
            (cityForm) =>
              getCanonicalFormKey(cityForm) === getCanonicalFormKey(countyForm),
          ),
      ),
    ];
  }

  // Ensure "noc" is added at the start if it exists
  if (countyData?.noc) {
    if (countyData?.noc_by_city?.[originalCityName]) {
      nocId = countyData?.noc_by_city?.[originalCityName]?.form_id;
      nocImg =
        countyData?.noc_by_city?.[originalCityName]?.img ||
        countyData?.noc_by_city?.[originalCityName]?.image_urls;
      combinedPermits = [
        { ...countyData?.noc_by_city?.[originalCityName] },
        ...combinedPermits,
      ];
    } else {
      nocId = countyData.noc?.form_id;
      nocImg = countyData?.noc?.img || countyData?.noc?.image_urls;
      combinedPermits = [{ ...countyData.noc }, ...combinedPermits];
    }
  }

  if (oldSelectedForms) {
    const oldSelectedFormKeys = new Set(
      oldSelectedForms.map(getCanonicalFormKey).filter(Boolean),
    );

    // Special logic to exclude forms the user has already selected, including stormwater check
    combinedPermits = combinedPermits.filter((permit) => {
      // Exclude already selected forms
      if (oldSelectedFormKeys.has(getCanonicalFormKey(permit))) {
        return false;
      }

      // Special logic for stormwater forms
      const isStormwater = permit.name.startsWith("stormwater");
      const stormwaterSelected = oldSelectedForms.some((selectedForm) =>
        selectedForm.startsWith("stormwater"),
      );

      // If a stormwater form has already been selected, exclude other stormwater forms
      if (isStormwater && stormwaterSelected) {
        return false;
      }

      // Include the permit if it passes all checks
      return true;
    });
  }

  if (!oldSelectedForms) {
    // Ensure "noc" is in the selectedForms EXCEPT for:
    // 1. Generator Permit (should be in Additional Forms, not pre-selected)
    // 2. When only pp_ntbo (or pp_ntbo bundle) is selected
    const isGeneratorPermit = selectedPermitType === "generator_permit";

    // Check if only NTBO bundle forms are selected
    const formsWithoutNoc = selectedForms.filter((form) => form !== "noc");
    const ntboBundle = ["pp_ntbo", "pp_ntbo_cover_sheet"];
    const onlyNtboBundleSelected =
      formsWithoutNoc.length > 0 &&
      formsWithoutNoc.every((form) => ntboBundle.includes(form));

    // Only auto-add NOC on initial empty selection.
    // This prevents re-adding NOC after user intentionally deselects it.
    if (
      nocId &&
      selectedForms.length === 0 &&
      !selectedForms.includes("noc") &&
      !isGeneratorPermit &&
      !onlyNtboBundleSelected
    ) {
      setSelectedForms([nocId, ...selectedForms]);
    }
  }

  // Sorting logic: Place forms in the order specified in formsOrder, keeping the rest at the end
  if (formsOrder) {
    combinedPermits.sort((a, b) => {
      const indexA = formsOrder.indexOf(a.label);
      const indexB = formsOrder.indexOf(b.label);

      if (indexA === -1 && indexB === -1) return 0; // Keep original order if both are not in the list
      if (indexA === -1) return 1; // Move `a` to the end if not in the list
      if (indexB === -1) return -1; // Move `b` to the end if not in the list

      return indexA - indexB; // Sort based on the order in formsOrder
    });
  }

  const permitFormIds = Object.entries(permitTypeFormMap)?.find(
    ([type]) => type === selectedPermitType,
  )?.[1];

  if (permitFormIds?.length > 0) {
    const groupedByPermitType = {};
    const usedFormNames = new Set();

    // Support multiple selected permit types
    const selectedTypes = Array.isArray(selectedPermitType)
      ? selectedPermitType
      : selectedPermitType
        ? [selectedPermitType]
        : [];

    selectedTypes.forEach((typeName) => {
      const matchedType = availablePermitTypes.find(
        (type) => type.name === typeName,
      );

      if (matchedType) {
        const matchedForms = combinedPermits.filter((form) =>
          permitFormIds.includes(form?.form_id),
        );

        // Remove duplicates (e.g. NOC already in list)
        const uniqueMatchedForms = Array.from(
          new Map(
            matchedForms.map((form) => [getCanonicalFormKey(form), form]),
          ).values(),
        );

        // Mark all used
        uniqueMatchedForms.forEach((form) =>
          usedFormNames.add(getCanonicalFormKey(form)),
        );

        // Sort if needed
        if (formsOrder) {
          uniqueMatchedForms.sort((a, b) => {
            const indexA = formsOrder.indexOf(a.label);
            const indexB = formsOrder.indexOf(b.label);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
        }
        groupedByPermitType[matchedType.label] = uniqueMatchedForms;
      }
    });

    // For generator_permit, include NOC and NTBO in Additional Forms section
    // For other permit types, show all remaining forms
    const isGeneratorPermit = selectedTypes.includes("generator_permit");

    let others;
    if (isGeneratorPermit) {
      // For generator permit, show NOC and NTBO in Additional Forms
      others = combinedPermits.filter((form) => {
        return form.form_id === nocId || form.name === "pp_ntbo";
      });
    } else {
      // For other permit types, show all forms except NOC/NOC Affidavit (they're already in the permit type group)
      others = combinedPermits.filter((form) => {
        return (
          !usedFormNames.has(getCanonicalFormKey(form)) &&
          !(form.form_id === nocId || form.name === "noc_affidavit")
        );
      });
    }

    // Sort "Others"
    if (formsOrder) {
      others.sort((a, b) => {
        const indexA = formsOrder.indexOf(a.label);
        const indexB = formsOrder.indexOf(b.label);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }

    if (others.length > 0) {
      groupedByPermitType["Others"] = others;
    }

    return {
      city: selectedCity,
      groupedPermits: groupedByPermitType,
      permits: combinedPermits,
      nocImg,
      nocId,
    };
  }

  return {
    city: selectedCity,
    permits: combinedPermits,
    nocImg,
    nocId,
  };
};
